// src/components/SubmitTime/utils/overtimeWarning.ts
// Overtime warning helpers — ported from legacy SubmitTime.js validateOvertimeWarning

import type { CustomFieldResponse } from '../types';

export interface WarningRecipient {
  personId: number;
  personName: string;
}

/** Custom fields whose people are tagged (and notified) in the overtime warning */
const WARNING_RECIPIENT_FIELDS = ['Assigned to', 'Approver'];

export const OVERTIME_60_MESSAGE =
  'Your remaining and your actual exceeds your initial estimate, please contact your supervisor.';
export const OVERTIME_100_MESSAGE =
  'You have exceeded the time based on your initial estimate, please contact your supervisor.';

/**
 * Returns the warning text when Actual exceeds Estimated by more than 60% (or 100%),
 * or null when no warning applies
 */
export function getOvertimeMessage(estimatedStr: string, actualStr: string): string | null
{
  if (!estimatedStr || !actualStr) return null;

  const estimated = parseFloat(estimatedStr);
  const actual = parseFloat(actualStr);

  // Without a positive estimate every submission would be "overtime"
  if (isNaN(estimated) || isNaN(actual) || estimated <= 0) return null;

  if (actual > estimated * 2)
  {
    return OVERTIME_100_MESSAGE;
  }
  if (actual > estimated * 1.6)
  {
    return OVERTIME_60_MESSAGE;
  }
  return null;
}

/** People in the "Assigned to" and "Approver" custom fields of the given layer */
export function getWarningRecipients(
  fieldsResponse: CustomFieldResponse[],
  layerId: number,
): WarningRecipient[]
{
  const layerFields = fieldsResponse.find(f => f.LayerId === layerId)?.CustomFields ?? [];
  const recipients: WarningRecipient[] = [];

  for (const fieldName of WARNING_RECIPIENT_FIELDS)
  {
    const field = layerFields.find(cf => cf.Name === fieldName);
    if (!field || field.Value === null || field.Value === undefined || field.Value === '')
    {
      continue;
    }

    const ids = String(field.Value)
      .split(',')
      .map(id => parseInt(id.trim(), 10));
    const names = (field.SelectedValuesText || '').split(',').map(name => name.trim());

    if (ids.length !== names.length)
    {
      console.error(`Overtime warning: ids and names mismatch in "${fieldName}"`);
      return [];
    }

    ids.forEach((personId, index) =>
    {
      if (isNaN(personId) || recipients.some(r => r.personId === personId)) return;
      recipients.push({ personId, personName: names[index] ?? '' });
    });
  }

  return recipients;
}

/** Warning reply body — SubmitReplyWarningId links it to the submit time reply */
export function buildWarningBody(
  submitReplyId: number,
  message: string,
  recipients: WarningRecipient[],
): string
{
  const tagsHtml = buildWarningTagsHtml(recipients);
  return (
    `<div class="detail-update-embed" style="padding: 0px !important" SubmitReplyWarningId="${submitReplyId}">` +
    `<div class="detail-update-description"><strong style='color: #e14949'>WARNING</strong></div>` +
    `<strong>${message}</strong>` +
    `<div><strong>${tagsHtml}</strong></div>` +
    `</div>`
  );
}

/** Person tags in the format parsed by getTagsInBody (span[person-id]) */
export function buildWarningTagsHtml(recipients: WarningRecipient[]): string
{
  return recipients
    .map(
      p =>
        `<span><span person-id="${p.personId}" style="color: rgb(98, 170, 203); font-weight: 600;">` +
        `${p.personName}</span></span>&nbsp;&nbsp;`,
    )
    .join('');
}
